import { CorrelationEndpointSource } from '../../@types/projects/types';
import { ProjectSample } from '../../@types/samples/types';

export type KrFamily = 'water_oil' | 'gas_oil';

export interface KrMissingSample {
  sample_uid: string;
  sample_code: string;
  missing_fields: string[];
}

export interface KrFamilyAvailability {
  requested_count: number;
  usable_count: number;
  unavailable_samples: KrMissingSample[];
}

export type KrSourceAvailability = Record<
  KrFamily,
  KrFamilyAvailability
>;

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const hasCurveEndpoint = (values: (number | null)[] | null | undefined) =>
  Boolean(values?.some(isNumber));

const missingWaterOil = (
  sample: ProjectSample,
  source: CorrelationEndpointSource
): string[] => {
  const input = sample.input_sample_values;
  const values = sample.k_rel_values?.water_oil;
  const missing: string[] = [];
  if (!isNumber(input.fzi)) missing.push('fzi');
  if (!isNumber(input.rqi)) missing.push('rqi');
  if (source === 'refinement') {
    const required = [
      'true_sor_w',
      'true_no_w',
      'true_nw',
      'true_krw',
      'true_kro_w'
    ] as const;
    return [...missing, ...required.filter(key => !isNumber(values?.[key]))];
  }
  if (!isNumber(values?.sor_w)) missing.push('sor_w');
  if (!isNumber(values?.no_w)) missing.push('no_w');
  if (!isNumber(values?.nw)) missing.push('nw');
  if (!isNumber(values?.krw_exponent) && !isNumber(input.krw_absolute)) {
    missing.push('krw_exponent');
  }
  if (
    !hasCurveEndpoint(sample.input_sample_curves.water_oil?.kro_lab) &&
    !isNumber(input.kro_absolute)
  ) {
    missing.push('kro_lab endpoint');
  }
  return missing;
};

const missingGasOil = (
  sample: ProjectSample,
  source: CorrelationEndpointSource
): string[] => {
  const input = sample.input_sample_values;
  const values = sample.k_rel_values?.gas_oil;
  const missing: string[] = [];
  if (!isNumber(input.fzi)) missing.push('fzi');
  if (!isNumber(input.rqi)) missing.push('rqi');
  if (source === 'refinement') {
    const required = [
      'true_sor_g',
      'true_no_g',
      'true_ng',
      'true_krg',
      'true_kro_g'
    ] as const;
    return [...missing, ...required.filter(key => !isNumber(values?.[key]))];
  }
  if (!isNumber(values?.sor_g)) missing.push('sor_g');
  if (!isNumber(values?.no_g)) missing.push('no_g');
  if (!isNumber(values?.ng)) missing.push('ng');
  if (!isNumber(values?.krg_exponent) && !isNumber(input.krg_absolute)) {
    missing.push('krg_exponent');
  }
  if (
    !hasCurveEndpoint(sample.input_sample_curves.gas_oil?.kro_lab) &&
    !isNumber(input.kro_absolute_g) &&
    !isNumber(input.kro_absolute)
  ) {
    missing.push('kro_lab endpoint');
  }
  return missing;
};

export const getKrSourceAvailability = (
  samples: ProjectSample[],
  selected: Pick<Record<KrFamily, string[]>, KrFamily>,
  source: CorrelationEndpointSource
): KrSourceAvailability => {
  const byUid = new Map(samples.map(sample => [sample.uid, sample]));
  const build = (family: KrFamily): KrFamilyAvailability => {
    const unavailable_samples: KrMissingSample[] = [];
    selected[family].forEach(uid => {
      const sample = byUid.get(uid);
      const missing_fields = sample
        ? family === 'water_oil'
          ? missingWaterOil(sample, source)
          : missingGasOil(sample, source)
        : ['sample'];
      if (missing_fields.length) {
        unavailable_samples.push({
          sample_uid: uid,
          sample_code: sample?.sample_code ?? uid,
          missing_fields
        });
      }
    });
    return {
      requested_count: selected[family].length,
      usable_count: selected[family].length - unavailable_samples.length,
      unavailable_samples
    };
  };
  return { water_oil: build('water_oil'), gas_oil: build('gas_oil') };
};
